import type {
  FetchFunction,
  OrderFunction,
  PageFunction,
  SelectFunction,
  SourceFilter
} from './types';

type CollectorStatus =
  | 'idle'
  | 'pending'
  | 'rejected'
  | 'failed'
  | 'success'
  | 'exhausted'
  | 'limitReached';

export interface CollectorMeta<R> {
  latestStatus: CollectorStatus;
  latestHTTPStatus: number | undefined;
  latestError: Error | unknown | undefined;
  latestRawData: R | undefined;
}

export interface Collector<R, E> {
  meta: CollectorMeta<R>;
  hasNext: boolean;
  getNext(length: number): Promise<CollectorMeta<R> & { data: E[] }>;
  reset(): void;
  rollback(items: E[]): void;
}

export interface CollectorConfig<R, E> {
  page: string | PageFunction<R>;
  select: SelectFunction<R, E[]>;
  order?: OrderFunction<E>;
  fetch?: FetchFunction;
  filter?: SourceFilter<E>;
  link?: string;
}

export function createCollector<R, E>(config: CollectorConfig<R, E>) {
  let latestStatus: CollectorStatus = 'idle';
  let latestHTTPStatus: number | undefined;
  let latestError: Error | unknown | undefined;
  let latestRawData: R | undefined;
  let fetchedCount = 0;
  let stack: E[] = [];
  const latestEmittedData: Set<E> = new Set();

  let nextPageURL =
    typeof config.page === 'function' ? config.page() : config.page;

  function getMeta() {
    return {
      latestStatus,
      latestHTTPStatus,
      latestError,
      latestRawData
    };
  }

  function flush() {
    latestHTTPStatus = undefined;
    latestError = undefined;
    latestRawData = undefined;

    latestEmittedData.clear();
  }

  async function getNext(length: number) {
    if (latestStatus === 'pending') {
      throw new Error('Collector is already in progress.');
    }

    if (latestStatus === 'exhausted') {
      throw new Error('Collector is exhausted.');
    }

    if (latestStatus === 'limitReached') {
      throw new Error('Collector is reached limit.');
    }

    latestStatus = 'pending';

    // memorize the latest meta value
    let httpStatus = latestHTTPStatus;
    let error = latestError;
    let rawData = latestRawData;

    // flush the latest meta and the latest emitted data
    flush();

    // this flag says whether fetching new data
    // was successfully or not
    let ok = true;

    const withFilters = () =>
      config.filter?.available
        ? fetchedCount < config?.filter?.sourcePostLimit
        : true;

    try {
      while (nextPageURL && stack.length < length && withFilters()) {
        const response = await (config.fetch ?? fetch)(nextPageURL);
        rawData = await response.json();
        httpStatus = response.status;

        if (!response.ok) {
          ok = false;
          break;
        }

        // pass the raw data through the select function to get data
        const parsedData = config
          .select(rawData as R)
          .map((item) => ({ ...item, sourceLink: config.link }));

        // Вычисляем, сколько еще сырых (неотфильтрованных) постов можем взять
        // до достижения лимита sourcePostsLimit (default 100)
        const remainingLimit = config.filter?.available
          ? Math.max(0, config.filter.sourcePostLimit - fetchedCount)
          : Infinity;

        // Отрезаем только нужное количество постов, чтобы не превысить лимит
        // Это защита от бесконечной загрузки при большом/бесконечном количестве данных
        const slicedForFilter = parsedData.slice(0, remainingLimit);

        // Увеличиваем счетчик сырых постов ДО применения фильтров
        // Это гарантирует, что мы не загрузим больше sourcePostLimit постов,
        // даже если фильтры отбросят большую часть данных
        fetchedCount += slicedForFilter.length;

        // Применяем фильтры к отрезанным данным
        // Количество отфильтрованных постов может быть меньше, чем slicedForFilter.length
        const filteredData = config.filter?.available
          ? (config.filter.applyFilters(slicedForFilter) ?? parsedData)
          : parsedData;

        // Добавляем отфильтрованные посты в стек для последующей выдачи
        stack = stack.concat(filteredData);

        // generate the next page url
        nextPageURL =
          typeof config.page === 'function' ? config.page(rawData) : undefined;
      }
    } catch (exception) {
      // memorize the thrown error
      error = exception;
      ok = false;
    }

    // apply the order
    if (config.order) {
      stack = config.order(stack);
    }

    // take the needed amount of the data
    const data = stack.slice(0, length);

    // put the rest of the data to the stack to use next time
    stack = stack.slice(length);

    if (error) {
      latestError = error;
      latestStatus = 'rejected';
    } else {
      latestHTTPStatus = httpStatus;
      latestRawData = rawData;

      if (!withFilters()) {
        latestStatus = 'limitReached';
      } else if (!nextPageURL && !stack.length) {
        latestStatus = 'exhausted';
      } else {
        latestStatus = ok ? 'success' : 'failed';
      }
    }

    // memorize the latest emitted data
    data.forEach((item) => {
      latestEmittedData.add(item);
    });

    return {
      ...getMeta(),
      data
    };
  }

  async function reset() {
    latestStatus = 'idle';
    stack = [];

    flush();
  }

  function rollback(items: E[]) {
    items.forEach((item) => {
      if (latestEmittedData.has(item)) {
        stack.push(item);
      }
    });
  }

  return {
    get meta() {
      return getMeta();
    },
    get hasNext() {
      return (
        latestStatus !== 'rejected' &&
        latestStatus !== 'failed' &&
        latestStatus !== 'exhausted' &&
        latestStatus !== 'limitReached'
      );
    },
    getNext,
    reset,
    rollback
  } as Collector<R, E>;
}
